import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, LoaderCircle, RotateCcw, Search } from 'lucide-react';

export interface PickerItem {
  value: string;
  label: string;
  /** Second line, e.g. the email under a user's name. */
  detail?: string;
}

export type PickerTone = 'red' | 'blue' | 'gray';

export interface PickerGroup {
  /** Unique across the whole picker. */
  key: string;
  label: string;
  /** A small badge after the label, e.g. "Reseller". */
  tag?: string;
  tone?: PickerTone;
  /** Shown after the label, e.g. the number of users. */
  count?: number;
  /** Undefined while not loaded (lazy groups). */
  items?: PickerItem[];
  loading?: boolean;
  failed?: boolean;
  groups?: PickerGroup[];
  /** Lazy groups: called when the group is expanded and its items aren't loaded (also the Retry after a failure). */
  onExpand?: () => void;
  /** Text when the group is loaded and has no items or sub-groups. */
  emptyText?: string;
}

interface GroupedPickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The empty value's text, e.g. "All users". */
  allLabel: string;
  /** What the closed picker shows for the current value (defaults to the value itself). */
  selectedLabel?: string;
  groups: PickerGroup[];
  /** Called once, the first time the panel opens (load the group list lazily). */
  onFirstOpen?: () => void;
  /** Shown instead of the groups: still loading the group list, or it failed. */
  status?: 'loading' | 'failed' | 'ready';
  failedText?: string;
  onRetry?: () => void;
  searchPlaceholder: string;
  /** Under the search box while searching, e.g. that only opened sections are searched. */
  searchHint?: string;
  /** Lets the search text itself be chosen (e.g. a typed email); return null when it isn't a valid value. */
  customItem?: (query: string) => PickerItem | null;
  /** Group keys expanded when the panel first opens. */
  initiallyExpanded?: string[];
  panelWidthClass?: string;
}

const TONE_CLASS: Record<PickerTone, string> = {
  red: 'border-red-800 bg-red-950/60 text-red-300',
  blue: 'border-sky-800 bg-sky-950/60 text-sky-300',
  gray: 'border-[#474646] bg-[#262626] text-gray-300',
};

const triggerClass =
  'flex h-[34px] w-full items-center justify-between gap-2 rounded-lg border border-[#474646] bg-[#1a1a1a] px-2.5 text-left text-sm text-white focus:outline-none focus:border-gray-400';

const matches = (item: PickerItem, query: string) =>
  [item.label, item.detail, item.value].some((text) => text?.toLowerCase().includes(query));

interface SearchHit {
  item: PickerItem;
  path: string;
}

/** Every loaded item matching the query, with the group path it was found under (first match per value). */
const searchGroups = (groups: PickerGroup[], query: string): SearchHit[] => {
  const hits: SearchHit[] = [];
  const seen = new Set<string>();
  const walk = (group: PickerGroup, path: string[]) => {
    const here = [...path, group.label];
    group.items?.forEach((item) => {
      if (!seen.has(item.value) && matches(item, query)) {
        seen.add(item.value);
        hits.push({ item, path: here.join(' › ') });
      }
    });
    group.groups?.forEach((child) => walk(child, here));
  };
  groups.forEach((group) => walk(group, []));
  return hits;
};

/**
 * A dropdown whose options are grouped in collapsible sections, nested to any depth, with search. Sections can load
 * their options lazily: a section with `onExpand` and no `items` asks for them the first time it is opened, so only
 * the sections a user actually opens cost an API call.
 */
const GroupedPicker = ({
  id,
  value,
  onChange,
  allLabel,
  selectedLabel,
  groups,
  onFirstOpen,
  status = 'ready',
  failedText,
  onRetry,
  searchPlaceholder,
  searchHint,
  customItem,
  initiallyExpanded = [],
  panelWidthClass = 'w-[380px]',
}: GroupedPickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initiallyExpanded));
  const opened = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const toggleOpen = () => {
    if (!open && !opened.current) {
      opened.current = true;
      onFirstOpen?.();
    }
    if (open) setQuery('');
    setOpen(!open);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const choose = (next: string) => {
    onChange(next);
    close();
  };

  const toggleGroup = (group: PickerGroup) => {
    const willExpand = !expanded.has(group.key);
    setExpanded((current) => {
      const next = new Set(current);
      if (willExpand) next.add(group.key);
      else next.delete(group.key);
      return next;
    });
    if (willExpand && group.onExpand && !group.items && !group.loading) group.onExpand();
  };

  const trimmedQuery = query.trim().toLowerCase();
  const hits = useMemo(() => (trimmedQuery ? searchGroups(groups, trimmedQuery) : []), [groups, trimmedQuery]);
  const custom = trimmedQuery && customItem ? customItem(query.trim()) : null;
  const showCustom = custom && !hits.some((hit) => hit.item.value.toLowerCase() === custom.value.toLowerCase());

  const renderItem = (item: PickerItem, depth: number, path?: string) => {
    const selected = item.value.toLowerCase() === value.toLowerCase();
    return (
      <button
        key={`${path ?? ''}|${item.value}`}
        type="button"
        onClick={() => choose(item.value)}
        aria-pressed={selected}
        className={`flex w-full items-start gap-2 rounded-md py-1.5 pr-2 text-left hover:bg-[#262626] ${selected ? 'bg-[#262626]' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        <span className="mt-0.5 w-4 shrink-0">{selected && <Check size={14} className="text-red-400" />}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-white">{item.label}</span>
          {item.detail && item.detail !== item.label && <span className="block truncate text-xs text-gray-400">{item.detail}</span>}
          {path && <span className="block truncate text-[11px] text-gray-500">{path}</span>}
        </span>
      </button>
    );
  };

  const renderGroup = (group: PickerGroup, depth: number): React.ReactNode => {
    const isOpen = expanded.has(group.key);
    const hasNothing = group.items !== undefined && group.items.length === 0 && !group.groups?.length;
    return (
      <div key={group.key}>
        <button
          type="button"
          onClick={() => toggleGroup(group)}
          aria-expanded={isOpen}
          className="flex w-full items-center gap-1.5 rounded-md py-1.5 pr-2 text-left hover:bg-[#262626]"
          style={{ paddingLeft: 4 + depth * 16 }}
        >
          {isOpen ? <ChevronDown size={14} className="shrink-0 text-gray-400" /> : <ChevronRight size={14} className="shrink-0 text-gray-400" />}
          <span className={`min-w-0 truncate text-sm ${depth === 0 ? 'font-semibold text-white' : 'text-gray-200'}`}>{group.label}</span>
          {group.tag && (
            <span className={`shrink-0 rounded border px-1.5 py-px text-[10px] uppercase tracking-wide ${TONE_CLASS[group.tone ?? 'gray']}`}>
              {group.tag}
            </span>
          )}
          {group.count !== undefined && <span className="ml-auto shrink-0 text-xs text-gray-500">{group.count}</span>}
        </button>
        {isOpen && (
          <div>
            {group.loading && (
              <div className="flex items-center gap-2 py-1.5 text-xs text-gray-400" style={{ paddingLeft: 28 + depth * 16 }}>
                <LoaderCircle size={12} className="animate-spin" />
                Loading…
              </div>
            )}
            {group.failed && !group.loading && (
              <div className="flex items-center gap-2 py-1.5 text-xs text-red-400" style={{ paddingLeft: 28 + depth * 16 }}>
                Couldn't load.
                {group.onExpand && (
                  <button type="button" onClick={group.onExpand} className="inline-flex items-center gap-1 text-gray-300 hover:text-white">
                    <RotateCcw size={12} /> Retry
                  </button>
                )}
              </div>
            )}
            {group.items?.map((item) => renderItem(item, depth + 1))}
            {group.groups?.map((child) => renderGroup(child, depth + 1))}
            {hasNothing && !group.loading && (
              <div className="py-1.5 text-xs text-gray-500" style={{ paddingLeft: 28 + depth * 16 }}>
                {group.emptyText ?? 'Nothing here.'}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (trimmedQuery) {
      return (
        <>
          {showCustom && renderItem(custom!, 0)}
          {hits.map((hit) => renderItem(hit.item, 0, hit.path))}
          {!showCustom && hits.length === 0 && <div className="px-2 py-3 text-xs text-gray-500">No matches.</div>}
        </>
      );
    }
    if (status === 'loading') {
      return (
        <div className="flex items-center gap-2 px-2 py-3 text-xs text-gray-400">
          <LoaderCircle size={12} className="animate-spin" />
          Loading…
        </div>
      );
    }
    if (status === 'failed') {
      return (
        <div className="flex items-center gap-2 px-2 py-3 text-xs text-red-400">
          {failedText ?? "Couldn't load."}
          {onRetry && (
            <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 text-gray-300 hover:text-white">
              <RotateCcw size={12} /> Retry
            </button>
          )}
        </div>
      );
    }
    return groups.map((group) => renderGroup(group, 0));
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={(event) => event.key === 'Escape' && open && (event.stopPropagation(), close())}>
      <button id={id} type="button" onClick={toggleOpen} aria-haspopup="dialog" aria-expanded={open} className={triggerClass}>
        <span className={`truncate ${value ? 'text-white' : 'text-gray-300'}`}>{value ? selectedLabel || value : allLabel}</span>
        <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-labelledby={id}
          className={`absolute left-0 z-30 mt-1 ${panelWidthClass} max-w-[calc(100vw-32px)] rounded-lg border border-[#474646] bg-[#141414] shadow-xl`}
        >
          <div className="border-b border-[#2a2a2a] p-2">
            <div className="flex items-center gap-2 rounded-md border border-[#474646] bg-[#1a1a1a] px-2">
              <Search size={14} className="shrink-0 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-[30px] w-full bg-transparent text-sm text-white placeholder:text-gray-500 focus:outline-none"
              />
            </div>
            {trimmedQuery && searchHint && <p className="mt-1.5 text-[11px] text-gray-500">{searchHint}</p>}
          </div>
          <div className="max-h-[360px] overflow-y-auto p-1">
            {!trimmedQuery && (
              <button
                type="button"
                onClick={() => choose('')}
                aria-pressed={!value}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[#262626] ${!value ? 'bg-[#262626]' : ''}`}
              >
                <span className="w-4 shrink-0">{!value && <Check size={14} className="text-red-400" />}</span>
                {allLabel}
              </button>
            )}
            {renderBody()}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedPicker;
